import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { usePermissions } from "../../hooks/usePermissions";

const dealerStatus = {
  DEALER_REGISTERED: "DEALER_REGISTERED",
  DEALER_ONB_PENDING: "DEALER_ONB_PENDING",
};

const RoleProtectedRoute = ({
  children,
  requiredRole,
  requiredRoles,
  requiredPermission,
  checkOnboardingStatus,
  fallbackPath = "/unauthorized",
}) => {
  const { currentUser, loading } = useUser();
  const { hasRole, hasAnyRole, hasPermission, canAccessRoute } =
    usePermissions();
  const authToken = localStorage.getItem("authToken");

  // Show loading while user data is being fetched
  if (loading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  // If no auth token, redirect to login
  if (!authToken) {
    return (
      <Navigate to="/" replace state={{ from: window.location.pathname }} />
    );
  }

  // If no user data, redirect to login
  if (!currentUser) {
    return (
      <Navigate to="/" replace state={{ from: window.location.pathname }} />
    );
  }

  // If no requirements specified, allow access (for public routes)
  if (!requiredRole && !requiredRoles && !requiredPermission) {
    return children ? children : <Outlet />;
  }

  // Check if user has access through role OR permission
  let hasAccess = false;

  // Check role-based access
  if (requiredRole && hasRole(requiredRole)) {
    hasAccess = true;
  }

  // Check multiple roles
  if (requiredRoles && hasAnyRole(requiredRoles)) {
    hasAccess = true;
  }

  // Check permission-based access
  if (requiredPermission && hasPermission(requiredPermission)) {
    hasAccess = true;
  }

  if (currentUser.status !== dealerStatus.DEALER_REGISTERED) {
    if (
      checkOnboardingStatus && currentUser.status === dealerStatus.DEALER_ONB_PENDING
    ) {
      return <Navigate to="/dealer-onboarding/dealer-details" replace />;
    }
  }

  // If no access through any method, deny access
  if (!hasAccess) {
    console.log("🔍 RoleProtectedRoute - Access denied:", {
      userRole: currentUser.role,
      requiredRole,
      requiredRoles,
      requiredPermission,
      hasRole: requiredRole ? hasRole(requiredRole) : false,
      hasAnyRole: requiredRoles ? hasAnyRole(requiredRoles) : false,
      hasPermission: requiredPermission
        ? hasPermission(requiredPermission)
        : false,
    });
    return (
      <Navigate
        to={fallbackPath}
        replace
        state={{ from: window.location.pathname }}
      />
    );
  }

  // If authenticated and authorized, render the route
  return children ? children : <Outlet />;
};

export default RoleProtectedRoute;
